import { FormLayout, Modal, TextField, Form, Select, Grid, List, Button } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import helpers from 'helpers/index';
import { lengthLessThan, notEmpty, useField, useForm } from '@shopify/react-form';
import { ReplaceIcon } from '@shopify/polaris-icons';
import { allowed_transaction_category } from 'interface/transaction.model';
import __ from 'languages/index';
import QuickSearchUser from 'components/quickSearchUser';
import QuickSearchWallet from 'components/quickSearchWallet';
import { useCreateTransaction } from 'queries/transaction.query';
import { useNotification } from 'context/NotificationContext';
import Capacity from 'components/Capacity';

/**
 *   Create upload Modal for Notification
 */

export default function TransactionNewModal({
  show,
  onClose,
  default_money_quantity,
  default_customer_id = '',
  order_id = '',
}: {
  show: boolean;
  onClose: Function;
  /** Default money quantity */
  default_money_quantity?: number;
  default_customer_id?: string;
  order_id?: string;
}) {
  const { mutateAsync: createEntity, isSuccess: updateSuccess } = useCreateTransaction();

  const { addNotification } = useNotification();

  const [selectedTransactionCategory, setSelectedTransactionCategory] = useState('general');
  const handleSelectTransactionCategoryChange = useCallback((value: string) => setSelectedTransactionCategory(value), []);
  const useFields = {
    wallet_id: useField<string>({
      value: '',
      validates: [notEmpty('Không được để trống trường này.')],
    }),

    transaction_value: useField<string>({
      value: String(default_money_quantity || ''),
      validates: [
        (inputVal) => {
          if (!inputVal || inputVal === '') {
            return 'Trường này không được để trống.';
          }
        },
      ],
    }),
    transaction_reference: useField<string>({
      value: String(order_id || ''),
      validates: [
        (inputVal) => {
          if (helpers.isUTF8(inputVal)) {
            return 'Trường này không được dùng mã UTF8.';
          }
        },
      ],
    }),

    transaction_note: useField<string>({
      value: '',
      validates: [lengthLessThan(1000, 'Không được dài quá 1000 ký tự.')],
    }),

    credit_user_id: useField<string>({
      value: '1',
      validates: [],
    }),
    debit_user_id: useField<string>({
      value: default_customer_id || '',
      validates: [],
    }),
  };

  const { fields, submit, submitting, dirty } = useForm({
    fields: useFields,

    async onSubmit(values) {
      try {
        // create new
        await createEntity({
          credit_user_id: values.credit_user_id,
          debit_user_id: values.debit_user_id,
          // order_pnr: order_pnr,
          wallet_id: values.wallet_id,
          amount: helpers.parseNumeric(values.transaction_value),
          transaction_note: values.transaction_note,
          transaction_category: selectedTransactionCategory,
          transaction_reference: values.transaction_reference,
        });
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });

  /* End form */

  const switchDebitAndCredit = useCallback(() => {
    //
    let current_debit_user_id = useFields.debit_user_id.value;
    let current_credit_user_id = useFields.credit_user_id.value;
    // hoan doi
    useFields.credit_user_id.onChange(current_debit_user_id);
    useFields.debit_user_id.onChange(current_credit_user_id);
  }, [useFields.credit_user_id, useFields.debit_user_id]);

  /**
   * Toast here meet an error ...
   */

  useEffect(() => {
    if (updateSuccess) {
      addNotification('info', __('Tạo giao dịch thành công.'));
      onClose();
    }
  }, [updateSuccess]);

  const optionForFilter = useMemo(() => {
    let r = [];
    for (let keyofFilter of allowed_transaction_category) {
      r.push({
        label: __(keyofFilter),
        value: keyofFilter,
      });
    }
    return r;
  }, []);

  const [showMyModal, setShowMyModal] = useState(false);
  useEffect(() => {
    setShowMyModal(show);
  }, [show]);

  return (
    <>
      <Modal
        open={showMyModal}
        onClose={() => onClose()}
        title={'Thêm giao dịch mới'}
        primaryAction={{
          content: 'Lưu lại',
          disabled: !dirty,
          loading: submitting,
          onAction: submit,
        }}
        secondaryActions={[
          {
            content: 'Đóng',
            onAction: () => onClose(),
          },
        ]}
      >
        <Modal.Section>
          <Capacity current_user_can={'manage_transactions'} onFail={<>Bạn không có quyền truy cập.</>}>
            <Form onSubmit={submit}>
              <FormLayout>
                <Grid>
                  <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <QuickSearchWallet
                      onClose={(v) => fields.wallet_id.onChange(v)}
                      current_wallet_id={fields.wallet_id.value}
                      title="Chọn ví"
                      error={fields.wallet_id.error}
                    />
                  </Grid.Cell>
                  <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <Select
                      label="Nhóm giao dịch"
                      options={optionForFilter}
                      onChange={handleSelectTransactionCategoryChange}
                      value={selectedTransactionCategory}
                    />
                  </Grid.Cell>
                </Grid>

                <FormLayout.Group>
                  <QuickSearchUser
                    title="Tài khoản ghi có (credit)"
                    // user_role=""
                    current_user_id={fields.credit_user_id.value}
                    onClose={(a) => fields.credit_user_id.onChange(String(a.user_id))}
                  />
                  <QuickSearchUser
                    title="Tài khoản ghi nợ (debit)"
                    // user_role=""
                    // readonly
                    current_user_id={fields.debit_user_id.value}
                    onClose={(a) => fields.debit_user_id.onChange(String(a.user_id))}
                  />
                </FormLayout.Group>
                <Button icon={ReplaceIcon} onClick={switchDebitAndCredit} variant="plain">
                  Đảo ngược hai tài khoản
                </Button>

                <List>
                  <List.Item>Ghi nợ cho tài khoản người dùng, được hiểu là trừ tiền</List.Item>
                  <List.Item>Ghi có cho tài khoản người dùng, được hiểu là cộng tiền</List.Item>
                </List>

                <TextField
                  label="Số tiền"
                  autoFocus
                  requiredIndicator
                  autoComplete="off"
                  value={helpers.formatNumberWithCommas(fields.transaction_value.value)}
                  onChange={(a) => fields.transaction_value.onChange(a)}
                  error={fields.transaction_value.error}
                />
                <TextField label="Mã đối ứng" autoComplete="off" {...fields.transaction_reference} />

                <TextField label="Ghi chú" autoComplete="off" showCharacterCount maxLength={1000} multiline={2} {...fields.transaction_note} />
              </FormLayout>
            </Form>
          </Capacity>
          <br />
        </Modal.Section>
      </Modal>
    </>
  );
}

